import * as React from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import useMediaQuery from '@mui/material/useMediaQuery'
import Popper from '@mui/material/Popper'
import { useTheme, styled } from '@mui/material/styles'
import { VariableSizeList, ListChildComponentProps } from 'react-window'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDataProvider } from 'react-admin'
import { ValueEditorProps } from 'react-querybuilder'
import { Checkbox } from '@mui/material'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from '../common/Button'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />
const selectAllOption = [{ name: 'Select All', id: -1 }]

const LISTBOX_PADDING = 8 // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props
  const element = data[index] as React.ReactElement & {
    props: {
      optionProps: any
      name: string
      selected: boolean
      selectedCount: number
      optionsCount: number
      isSelectAll?: boolean
    }
    key: string | number | null
  }
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  }

  const optionProps = element.props.optionProps || {}
  const key = element.key as any
  const { name } = element.props
  const selected = !!element.props.selected
  const selectedCount = element.props.selectedCount ?? 0
  const optionsCount = element.props.optionsCount ?? 0
  const isSelectAll = !!element.props.isSelectAll

  const isChecked = isSelectAll ? selectedCount === optionsCount : selected

  return (
    <Typography
      key={key as any}
      component="li"
      {...optionProps}
      noWrap
      style={inlineStyle}
    >
      <Checkbox
        sx={{ '&.MuiCheckbox-root': { padding: 'unset' } }}
        icon={icon}
        checkedIcon={checkedIcon}
        style={{ marginRight: 8 }}
        checked={isChecked}
      />
      {name}
    </Typography>
  )
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props
  // Materialize all option rows into a flat array as recommended by MUI's virtualization example.
  // React.Children.toArray will flatten fragments and nested arrays preserving order.
  const itemData = React.Children.toArray(
    children
  ) as React.ReactElement<unknown>[]

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getHeight = () => {
    // Show more rows by default to make it clear there are many results
    const maxVisibleRows = 12
    if (itemCount > maxVisibleRows) {
      return maxVisibleRows * itemSize
    }
    return itemData.length * itemSize
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={10}
          itemCount={itemCount}
          itemKey={(index, data: any) => {
            try {
              const k = data[index]?.key
              return typeof k !== 'undefined' ? k : index
            } catch (e) {
              return index
            }
          }}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

// export default function VirtualizedAutoComplete(props: ValueEditorProps) {
const USE_VIRTUALIZATION = false

export default function VirtualizedAutoComplete({
  value,
  label,
  filter = null,
  handleOnChange,
  dpFetchValue,
  variant = 'standard',
  size = 'medium',
  maxSelected,
  showSelectAll = true,
  selectedOptions,
}: {
  value: ValueEditorProps['value']
  label: string
  filter?: any
  handleOnChange: ValueEditorProps['handleOnChange']
  dpFetchValue: string
  variant?: 'standard' | 'outlined'
  size?: 'small' | 'medium'
  maxSelected?: number
  showSelectAll?: boolean
  selectedOptions?: any[]
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [options, setOptions] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [hasMore, setHasMore] = useState(true)
  const [search, setSearch] = useState('')
  const dp = useDataProvider()
  const [selectedValues, setSelectedValues] = useState<any[]>([])
  const fetchTimeoutRef = React.useRef<number | null>(null)

  const resetPaging = useCallback(() => {
    setPage(1)
    setPerPage(50)
    setHasMore(true)
    setOptions([])
  }, [])

  const fetchOptions = useCallback(
    async (nextPage?: number, nextPerPage?: number) => {
      const pg = nextPage ?? page
      const pp = nextPerPage ?? perPage
      setLoading(true)
      try {
        const resp = await dp.getList(dpFetchValue, {
          filter: { ...(filter || {}), ...(search ? { q: search } : {}) },
          sort: { field: 'name', order: 'asc' },
          pagination: { page: pg, perPage: pp },
        })
        const newItems = resp.data || []
        setOptions((prev) => (pg === 1 ? newItems : [...prev, ...newItems]))
        // If returned fewer than requested, no more pages
        setHasMore(newItems.length >= pp)
      } finally {
        setLoading(false)
      }
    },
    [dp, dpFetchValue, filter, page, perPage, search]
  )

  // Load selected values for label display
  useEffect(() => {
    if (selectedOptions) {
      const ids = (value as any[]) ?? []
      setSelectedValues(
        selectedOptions.filter((option) => ids.includes(option.id))
      )
      setDataLoading(false)
      return
    }
    if (value && (value as any[]).length) {
      setDataLoading(true)
      dp.getMany(dpFetchValue, { ids: value as any[] }).then(
        (response: any) => {
          setSelectedValues(response.data)
          setDataLoading(false)
        }
      )
    } else {
      setSelectedValues([])
      setDataLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dp, dpFetchValue, label, selectedOptions, value])

  // Fetch when opened and when page/perPage/search changes
  useEffect(() => {
    if (open) {
      fetchOptions()
    }
  }, [open, page, perPage, search, fetchOptions])

  const optionsWithSelectAll = useMemo(() => {
    if (!options.length) return []
    if (!showSelectAll) {
      return options
    }
    if (typeof maxSelected === 'number' && options.length > maxSelected) {
      return options
    }
    return selectAllOption.concat(options)
  }, [maxSelected, options, showSelectAll]) as any[]

  const onSelectAll = () => {
    if (!showSelectAll) return
    if (typeof maxSelected === 'number' && options.length > maxSelected) {
      return
    }
    if (selectedValues.length === options.length) {
      setSelectedValues([])
      handleOnChange([])
    } else {
      setSelectedValues(options)
      handleOnChange(options.map((val) => val.id))
    }
  }

  const onChange = (_: any, changeValue: any[]) => {
    if (changeValue.find((tag) => tag.id === -1)) {
      onSelectAll()
    } else {
      if (typeof maxSelected === 'number' && changeValue.length > maxSelected) {
        return
      }
      setSelectedValues(changeValue)
      handleOnChange(changeValue.map((val) => val.id))
    }
  }

  const inputTextPlaceholder = `Enter "${label}"`

  const [inputValue, setInputValue] = useState('')
  const searchDebounceRef = React.useRef<number | null>(null)

  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    // Preserve the typed text when selecting an option: MUI fires reason === 'reset'
    // We only update the input/search when the user actually types (reason === 'input')
    // or explicitly clears (reason === 'clear').
    if (reason === 'reset') {
      return
    }

    if (reason === 'clear') {
      setInputValue('')
      if (searchDebounceRef.current)
        window.clearTimeout(searchDebounceRef.current)
      // Immediately clear the backend search and reset paging on clear
      setSearch('')
      resetPaging()
      return
    }

    // reason === 'input'
    setInputValue(newInputValue)
    if (searchDebounceRef.current)
      window.clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = window.setTimeout(() => {
      setSearch(newInputValue)
      resetPaging()
    }, 300)
  }

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const list = e.currentTarget
    if (
      list.scrollTop + list.clientHeight >= list.scrollHeight - 48 &&
      hasMore &&
      !loading
    ) {
      if (fetchTimeoutRef.current) window.clearTimeout(fetchTimeoutRef.current)
      fetchTimeoutRef.current = window.setTimeout(() => {
        setPage((p) => p + 1)
      }, 250)
    }
  }

  const filterKey = useMemo(() => JSON.stringify(filter || {}), [filter])

  useEffect(() => {
    // Reset when filter changes externally (e.g., selecting a different field)
    resetPaging()
  }, [label, filterKey, resetPaging])

  const renderInput = (params) => {
    const numSelected = selectedValues.length
    const placeholderText =
      loading || dataLoading ? 'Loading...' : inputTextPlaceholder
    const isMaxSelected =
      typeof maxSelected === 'number' && numSelected >= maxSelected
    const selectedBadge = numSelected ? (
      <Button
        className={
          isMaxSelected
            ? 'py-[2px] px-5 bg-red-500 border-none font-[400] cursor-default'
            : 'py-[2px] px-5 bg-[#2C7CD7] border-none font-[400] cursor-default'
        }
      >
        {isMaxSelected ? 'Max Selected' : `${numSelected} Selected`}
      </Button>
    ) : null
    const outlinedSx =
      variant === 'outlined'
        ? {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
              minHeight: '40px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
          }
        : undefined

    const mergedInputProps = {
      ...params.InputProps,
      startAdornment: selectedBadge ? (
        <span className="mr-2">{selectedBadge}</span>
      ) : (
        params.InputProps.startAdornment
      ),
    }
    const textFieldProps = {
      ...params,
      variant,
      size,
      sx: outlinedSx,
      inputProps: { ...params.inputProps, sx: { cursor: 'pointer' } },
      placeholder: placeholderText,
      InputProps: mergedInputProps,
    }

    return <TextField {...textFieldProps} />
  }

  return (
    <Autocomplete
      multiple
      id="autocomplete-virtualized"
      sx={{ width: 300 }}
      disableListWrap
      // Allow interaction; show loading state in UI instead of disabling the whole control
      PopperComponent={StyledPopper}
      ListboxComponent={USE_VIRTUALIZATION ? ListboxComponent : undefined}
      ListboxProps={{ onScroll: handleScroll }}
      options={optionsWithSelectAll}
      onChange={onChange}
      value={selectedValues}
      renderInput={renderInput}
      getOptionLabel={(option: any) => option.name}
      isOptionEqualToValue={(option: any, testValue: any) =>
        option.id === testValue.id
      }
      renderOption={(renderOptionProps, option, state) => {
        const isSelectAll = option.id === -1
        if (USE_VIRTUALIZATION) {
          // Virtualized: return a single lightweight element carrying props for the row renderer
          return React.createElement(
            'div',
            {
              key: option.id,
              ...({
                optionProps: renderOptionProps,
                name: option.name,
                selected: state.selected,
                selectedCount: selectedValues.length,
                optionsCount: options.length,
                isSelectAll,
              } as any),
            },
            null
          )
        }
        // Non-virtualized fallback: render a proper <li> with MUI-provided props so selection/click works
        return (
          <li
            {...renderOptionProps}
            key={option.id}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Checkbox
              sx={{ '&.MuiCheckbox-root': { padding: 'unset' }, mr: 1 }}
              icon={icon}
              checkedIcon={checkedIcon}
              checked={
                isSelectAll
                  ? selectedValues.length === options.length
                  : state.selected
              }
            />
            {option.name}
          </li>
        )
      }}
      renderTags={() => null}
      fullWidth
      disableCloseOnSelect
      popupIcon={
        <ChevronDownIcon className="h-3 w-3 text-black stroke-[3.5]" />
      }
      style={{ minWidth: '500px', maxWidth: '500px' }}
      open={open}
      onOpen={() => {
        setOpen(true)
        // On first open with empty options, reset and proactively fetch page 1 to avoid timing issues
        if (options.length === 0) {
          resetPaging()
          // Proactive fetch to ensure options appear immediately even if effect timing varies
          fetchOptions(1, 50)
        }
      }}
      onClose={() => setOpen(false)}
      loading={loading}
      loadingText="Loading..."
      noOptionsText={loading ? 'Loading...' : 'No options'}
      inputValue={inputValue}
      onInputChange={handleInputChange}
    />
  )
}
